import React from 'react';
import { Formik, Form, FormikProps } from 'formik';
import classNames from 'classnames';
import _ from 'lodash';

import { useAppDispatch, useTranslation } from 'client/hooks';
import { leadApi } from 'client/redux/api/lead';
import { updateParamsSchema } from 'endpoints/lead';
import { REG_EXP } from 'client/constants';

import TextFieldInput from '../TextFieldInput';
import TextArea from '../TextArea';
import MediaField from '../MediaField';
import Button from '../Button';
import Checkbox from '../Checkbox';
import RadioButtonsGroup from '../RadioButtonsGroup';
import { dataLayerEvents } from 'client/utils/gtag';
import { setPopUp } from 'client/redux/ui';

import css from './Form.module.scss';

interface Props {
  className?: string;
}

type FormData = {
  fallenName: string;
  fullName: string;
  email: string;
  phone: string;
  photoCredit: string;
  story: string;
  songName: string;
  image: string;
  connectionType: string;
  confirmation: boolean;
};

const formValues: FormData = {
  fallenName: '',
  fullName: '',
  email: '',
  phone: '',
  photoCredit: '',
  story: '',
  songName: '',
  image: '' as string & File,
  connectionType: '',
  confirmation: false,
};

let createLeadAttemptsCount = 0;
let sendValues: FormData | null = null;

const FormMain: React.FC<Props> = () => {
  const { translate } = useTranslation('form');
  const [createLead, createLeadResult] = leadApi.endpoints.createLead.useMutation();
  const formikRef = React.useRef<FormikProps<FormData>>(null);
  const dispatch = useAppDispatch();
  const dataLayerInstance = new dataLayerEvents();
  const radioBtnsValues = [translate('radioButtons.firstClose'), translate('radioButtons.secondClose')];
  React.useEffect(() => {
    if (
      (createLeadResult.status === 'fulfilled' && !createLeadResult.data?.success) ||
      createLeadResult.status === 'rejected'
    ) {
      if (createLeadAttemptsCount < 3) {
        if (sendValues) createLead(sendValues);
        createLeadAttemptsCount++;
      } else {
        sendValues = null;
        createLeadAttemptsCount = 0;
      }
    }

    if (createLeadResult.data?.success) {
      dataLayerInstance.onFormSent(sendValues?.fallenName || '');
      sendValues = null;
      createLeadAttemptsCount = 0;
    }
  }, [createLeadResult, dispatch]);

  const handleSubmit = async (values: FormData) => {
    createLead({ ...values });
    sendValues = { ...values };
    createLeadAttemptsCount++;
    setTimeout(() => {
      dispatch(setPopUp('success'));
      const formik = formikRef.current;
      formik?.resetForm({ values: _.cloneDeep(formValues) });
    }, 1500);
  };

  return (
    <div className={css.formWrapper}>
      <div className={css.form}>
        <Formik
          innerRef={formikRef}
          initialValues={_.cloneDeep(formValues)}
          onSubmit={handleSubmit}
          validationSchema={updateParamsSchema}
          // validateOnChange={true}
          validateOnBlur={true}
        >
          {({ ...rest }) => {
            return (
              <Form>
                <div className={classNames(css.fieldsWrapper)}>
                  <div className={classNames(css.fieldWrapper, css.short)}>
                    <TextFieldInput
                      name="fallenName"
                      placeholder={translate('textFields.fallenName')}
                      pattern={REG_EXP.HEBREW_ONLY_LETTERS}
                    />
                  </div>
                  <div className={classNames(css.fieldWrapper, css.short)}>
                    <TextFieldInput
                      name="fullName"
                      placeholder={translate('textFields.fullName')}
                      pattern={REG_EXP.HEBREW_ONLY_LETTERS}
                    />
                  </div>
                  <div className={classNames(css.fieldWrapper, css.short)}>
                    <TextFieldInput name="email" placeholder={translate('textFields.email')} pattern={REG_EXP.EMAIL} />
                  </div>
                  <div className={classNames(css.fieldWrapper, css.short)}>
                    <TextFieldInput
                      name="songName"
                      placeholder={translate('textFields.songName')}
                      pattern={REG_EXP.HEBREW_ONLY_LETTERS}
                    />
                  </div>
                </div>
                <div className={classNames(css.fieldsWrapper)}>
                  <div className={classNames(css.fieldWrapper)}>
                    <TextArea name="story" placeholder={translate('textFields.story')} />
                  </div>
                </div>
                <div className={classNames(css.fieldsWrapper)}>
                  <div className={classNames(css.fieldWrapper, css.short)}>
                    <TextFieldInput
                      name="phone"
                      placeholder={translate('textFields.phone')}
                      pattern={REG_EXP.NUMBERS}
                    />
                  </div>
                  <div className={classNames(css.fieldWrapper, css.short)}>
                    <MediaField name="image" placeholder={translate('mediafield.image')} />
                  </div>
                  <div className={classNames(css.fieldWrapper, css.short)}>
                    <TextFieldInput
                      name="photoCredit"
                      placeholder={translate('textFields.photoCredit')}
                      pattern={REG_EXP.HEBREW_ONLY_LETTERS}
                    />
                  </div>
                  <div className={classNames(css.fieldWrapper, css.short, css.checkboxWrapper)}>
                    <RadioButtonsGroup
                      name="connectionType"
                      values={radioBtnsValues}
                      error={rest.errors && rest.errors.connectionType ? rest.errors.connectionType : ''}
                    />
                  </div>
                </div>

                <div className={classNames(css.submitWrapper)}>
                  <Checkbox
                    name="confirmation"
                    label={translate('checkboxes.confirmation')}
                    isBig={false}
                    className={css.confirmation}
                  />

                  <Button
                    type="submit"
                    disabled={rest.isSubmitting}
                    className={css.submitButton}
                    text={translate('submitButton')}
                  />
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default FormMain;
